import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion, useScroll } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { ProductCard } from '../shared/ProductCard';
import { HeadingText } from '../shared/HeadingText';
import { useScrollEnds } from '../../hooks/useScrollEnds';
import { usePages } from '../../contexts/SiteContext';
import { fill } from '../../utils/copy';
import { quietLink, sectionHeading } from '../../ui/sections';
import type { Product } from '../../core/types/catalog';
type Props = {
  products: Product[];
  total: number;
};
const arrowBtn = 'flex h-12 w-12 items-center justify-center rounded-full border border-ink/20 text-ink transition-[background-color,color,border-color,opacity] duration-150 ease-soft hover:border-ink hover:bg-ink hover:text-shell disabled:pointer-events-none disabled:opacity-30';

/**
 * "Wat ik het vaakst meegeef": de salonfavorieten als plank waar je langs schuift. Vegen,
 * scrollen of de pijlen (die uitgaan aan het begin en eind); een haarlijn toont hoe ver je
 * bent. De laatste kaart steekt op elk scherm half uit, zodat je ziet dat er meer staat.
 */
export function HomeShelf({
  products,
  total
}: Props) {
  const pages = usePages();
  const railRef = useRef<HTMLUListElement>(null);
  const reduce = useReducedMotion();
  const {
    scrollXProgress
  } = useScroll({
    container: railRef
  });
  const {
    atStart,
    atEnd
  } = useScrollEnds(railRef);
  const copy = pages.home.bestsellers;
  const nudge = (dir: 1 | -1) => {
    const rail = railRef.current;
    if (!rail) return;
    rail.scrollBy({
      left: dir * rail.clientWidth * 0.8,
      behavior: reduce ? 'auto' : 'smooth'
    });
  };
  return <section className="overflow-hidden bg-sand py-20 sm:py-24 lg:py-32" aria-labelledby="home-bestsellers" data-id="element-7473" data-mp="17ibyw1ihs0lg">
      <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-7474" data-mp="12iz14lq31943">
        <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-7475" data-mp="12kjvaa8hiq1f">
          <div className="max-w-2xl" data-id="element-7476" data-mp="xkecljk70dvh">
            <h2 id="home-bestsellers" className={`${sectionHeading} text-ink`} data-id="element-7477" data-mp="nwa3yqjaz80v">
              <HeadingText heading={copy.heading} data-id="element-7478" data-mp="nyd4w5lzulkd" />
            </h2>
            {copy.body && <p className="mt-5 max-w-xl text-[17px] leading-relaxed text-muted" data-id="element-7479" data-mp="sweocrnc0mwd">{fill(copy.body)}</p>}
          </div>
          <div className="flex items-center gap-6" data-id="element-7480" data-mp="exoo5357u73s">
            {copy.cta && <Link to="/webshop" className={`${quietLink} text-ink`} data-id="element-7481" data-mp="ix791yx1ei6m">
                {fill(copy.cta, {
              count: total
            })}
                <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7482" data-mp="1bll90k7orokt" />
              </Link>}
            <div className="hidden gap-2 sm:flex" data-id="element-7483" data-mp="xupy72dmmxel">
              <button type="button" onClick={() => nudge(-1)} disabled={atStart} aria-label="Vorige producten" aria-controls="home-shelf" className={arrowBtn} data-id="element-7484" data-mp="xty2lrk14zob">
                <ArrowLeftIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7485" data-mp="1pkb63zui9o96" />
              </button>
              <button type="button" onClick={() => nudge(1)} disabled={atEnd} aria-label="Volgende producten" aria-controls="home-shelf" className={arrowBtn} data-id="element-7486" data-mp="1ucvycwxrjtal">
                <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7487" data-mp="3a3ocnqe46ym1" />
              </button>
            </div>
          </div>
        </div>

        <ul id="home-shelf" ref={railRef} aria-label="Salonfavorieten" className="-mx-6 mt-12 flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-px-6 px-6 pb-2 [scrollbar-width:none] sm:gap-6 lg:-mx-10 lg:mt-14 lg:scroll-px-10 lg:px-10 [&::-webkit-scrollbar]:hidden" data-id="element-7488" data-mp="3orj658zy5706">
          {products.map(p => <li key={p.id} className="w-[68%] shrink-0 snap-start sm:w-[40%] lg:w-[calc((100%-4.5rem)/3.5)]" data-id="element-7489" data-mp="36235mlo9hejr">
              <ProductCard product={p} data-id="element-7490" data-mp="311ouuvv9zqi1" />
            </li>)}
        </ul>

        {!(atStart && atEnd) && <div aria-hidden="true" className="mt-10 h-px w-full bg-ink/15" data-id="element-7491" data-mp="la127cnaok51">
            <motion.div style={{
          scaleX: scrollXProgress
        }} className="h-px origin-left bg-ink" data-id="element-7492" data-mp="gbfzn6kklodf" />
          </div>}
      </div>
    </section>;
}